<template>
  <div class="patient-chart-view">
    <nav class="breadcrumbs">
      <router-link to="/learning/patients">Patients</router-link>
      <span class="breadcrumb-sep">/</span>
      <span class="breadcrumb-current">{{ patientName }}</span>
    </nav>

    <div class="chart-header">
      <div class="header-left">
        <button type="button" class="btn btn-ghost back-btn" @click="goBack">
          <i class="fas fa-arrow-left"></i> Back to patients
        </button>
        <div class="title-block">
          <h1>{{ patientName }}</h1>
          <p v-if="demographicsLine" class="subtitle">{{ demographicsLine }}</p>
        </div>
      </div>
      <div class="header-actions">
        <router-link
          :to="{
            path: '/learning',
            query: { tab: 'agent', compliance_subject_id: subjectId },
          }"
          class="btn btn-primary"
        >
          Vet Consult
        </router-link>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i> Loading patient chart…
    </div>

    <div v-else-if="error" class="error-state">
      <i class="fas fa-exclamation-triangle"></i>
      <p>{{ error }}</p>
      <button class="btn btn-secondary" @click="loadChart">Retry</button>
    </div>

    <template v-else>
      <ExamDictationPanel
        :subject-id="subjectId"
        :patient-name="patientName"
        :open-drafts="soapDrafts"
        @saved="loadChart"
      />

      <section class="search-panel">
        <label for="clinical-search"><i class="fas fa-search"></i> Search chart</label>
        <div class="search-row">
          <input
            id="clinical-search"
            v-model="searchQuery"
            type="search"
            class="form-control"
            placeholder="Medications, vaccines, SOAP notes…"
            @keyup.enter="runSearch"
          />
          <button class="btn btn-secondary" :disabled="searching" @click="runSearch">
            {{ searching ? 'Searching…' : 'Search' }}
          </button>
        </div>
        <div v-if="searchResults.length" class="search-results">
          <div
            v-for="(hit, idx) in searchResults"
            :key="`${hit.document_id}-${idx}`"
            class="search-hit"
          >
            <strong>{{ hit.document_name || 'Clinical history' }}</strong>
            <span v-if="hit.document_type" class="doc-type">{{ hit.document_type }}</span>
            <ul>
              <li v-for="(h, hi) in hit.hits" :key="hi">{{ h.snippet || h.field }}</li>
            </ul>
          </div>
        </div>
      </section>

      <div class="chart-grid">
        <section class="panel demographics-panel">
          <h2><i class="fas fa-id-card"></i> Demographics</h2>
          <dl class="demo-list">
            <dt>Species</dt><dd>{{ ctx.species || '—' }}</dd>
            <dt>Breed</dt><dd>{{ ctx.breed || '—' }}</dd>
            <dt>Sex</dt><dd>{{ formatSex(ctx.sex) }}</dd>
            <dt>Weight</dt><dd>{{ ctx.weight_kg ? `${ctx.weight_kg} kg` : '—' }}</dd>
            <dt>Date of birth</dt><dd>{{ ctx.date_of_birth || '—' }}</dd>
            <dt>Microchip</dt><dd>{{ ctx.microchip || '—' }}</dd>
          </dl>
        </section>

        <section class="panel history-panel">
          <h2><i class="fas fa-syringe"></i> Vaccinations</h2>
          <ul v-if="vaccinations.length" class="line-list">
            <li v-for="(vax, i) in vaccinations" :key="i">
              <strong>{{ vax.name || 'Vaccine' }}</strong>
              <span v-if="vax.date"> · {{ vax.date }}</span>
              <span v-if="vax.next_due"> · next due {{ vax.next_due }}</span>
            </li>
          </ul>
          <p v-else class="empty-hint">No vaccination records yet.</p>

          <h2 class="section-gap"><i class="fas fa-pills"></i> Medications</h2>
          <ul v-if="medications.length" class="line-list">
            <li v-for="(med, i) in medications" :key="i">
              <strong>{{ med.name || 'Medication' }}</strong>
              <span v-if="med.dose"> · {{ med.dose }}</span>
              <span v-if="med.route"> · {{ med.route }}</span>
            </li>
          </ul>
          <p v-else class="empty-hint">No prescriptions on chart.</p>

          <h2 class="section-gap"><i class="fas fa-notes-medical"></i> SOAP Notes</h2>
          <ul v-if="soapNotes.length" class="line-list">
            <li v-for="(note, i) in soapNotes" :key="i">
              <p class="note-summary">{{ note.summary || note.value || 'SOAP note' }}</p>
              <p v-if="note.subjective" class="soap-line">S: {{ note.subjective }}</p>
              <p v-if="note.objective" class="soap-line">O: {{ note.objective }}</p>
              <p v-if="note.assessment" class="soap-line">A: {{ note.assessment }}</p>
              <p v-if="note.plan" class="soap-line">P: {{ note.plan }}</p>
              <p v-if="note.mentioned_meds" class="soap-line">Meds noted: {{ note.mentioned_meds }}</p>
              <span v-if="note.date" class="muted">{{ note.date }}</span>
            </li>
          </ul>
          <p v-else class="empty-hint">No SOAP notes yet.</p>
        </section>

        <section class="panel documents-panel">
          <h2><i class="fas fa-file-medical"></i> Documents ({{ documents.length }})</h2>
          <ul v-if="documents.length" class="doc-list">
            <li v-for="doc in documents" :key="doc.id">
              <span class="doc-name">{{ doc.name }}</span>
              <span class="doc-type">{{ doc.document_type }}</span>
              <span v-if="doc.clinical_summary" class="doc-summary">{{ doc.clinical_summary }}</span>
            </li>
          </ul>
          <p v-else class="empty-hint">Upload clinical documents to build the chart.</p>
        </section>
      </div>
    </template>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import api from '@/services/api';
import ExamDictationPanel from '@/components/veterinary/ExamDictationPanel.vue';

export default {
  name: 'PatientChartView',
  components: { ExamDictationPanel },
  props: {
    projectId: { type: [String, Number], required: false, default: null },
    subjectId: { type: [String, Number], required: true },
  },
  data() {
    return {
      loading: true,
      error: null,
      chart: {},
      searchQuery: '',
      searchResults: [],
      searching: false,
      debouncedSearch: null,
    };
  },
  computed: {
    ctx() {
      return this.chart.patient_context || {};
    },
    patientName() {
      return this.chart.entity?.name || this.ctx.patient_name || 'Patient';
    },
    demographicsLine() {
      const parts = [this.ctx.species, this.ctx.breed].filter(Boolean);
      return parts.join(' · ');
    },
    history() {
      return this.chart.clinical_history || this.ctx.clinical_history || {};
    },
    vaccinations() {
      return this.history.vaccinations || [];
    },
    medications() {
      return this.history.medications || [];
    },
    soapNotes() {
      return this.history.soap_notes || [];
    },
    soapDrafts() {
      return this.chart.soap_drafts || [];
    },
    documents() {
      return this.chart.documents || [];
    },
  },
  watch: {
    subjectId() {
      this.searchQuery = '';
      this.searchResults = [];
      this.loadChart();
    },
  },
  mounted() {
    this.debouncedSearch = debounce(this.runSearch, 300);
    this.loadChart();
  },
  methods: {
    async loadChart() {
      this.loading = true;
      this.error = null;
      try {
        const res = await api.get(`/veterinary/patients/${this.subjectId}/chart/`);
        this.chart = res.data || {};
      } catch (err) {
        this.error = err.response?.data?.error || 'Could not load patient chart.';
      } finally {
        this.loading = false;
      }
    },
    async runSearch() {
      const query = this.searchQuery.trim();
      if (!query) {
        this.searchResults = [];
        return;
      }
      this.searching = true;
      try {
        const res = await api.post(`/veterinary/patients/${this.subjectId}/search/`, {
          query,
          limit: 50,
        });
        this.searchResults = res.data?.results || [];
      } catch (err) {
        console.error('Clinical search failed:', err);
        this.searchResults = [];
      } finally {
        this.searching = false;
      }
    },
    formatSex(sex) {
      if (!sex) return '—';
      return sex.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
    },
    goBack() {
      this.$router.push({ name: 'LearningPatients' });
    },
  },
};
</script>

<style scoped>
.patient-chart-view {
  max-width: 1120px;
  margin: 0;
  padding: 0 0 1.5rem;
}

.breadcrumbs {
  font-size: 0.875rem;
  margin-bottom: 1rem;
  color: var(--text-muted, #64748b);
}

.breadcrumb-sep {
  margin: 0 0.35rem;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.title-block h1 {
  margin: 0;
  font-size: 1.75rem;
}

.subtitle {
  margin: 0.25rem 0 0;
  color: var(--text-muted, #64748b);
}

.search-panel {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--surface-muted, #f8fafc);
  border-radius: 8px;
}

.search-row {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.search-row .form-control {
  flex: 1;
}

.search-results {
  margin-top: 1rem;
}

.search-hit {
  padding: 0.75rem 0;
  border-top: 1px solid var(--border-color, #e2e8f0);
}

.chart-grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 1rem;
}

@media (max-width: 900px) {
  .chart-grid {
    grid-template-columns: 1fr;
  }
}

.panel {
  background: #fff;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  padding: 1rem 1.25rem;
}

.documents-panel {
  grid-column: 1 / -1;
}

.panel h2 {
  font-size: 1rem;
  margin: 0 0 0.75rem;
}

.section-gap {
  margin-top: 1.25rem;
}

.demo-list {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.35rem 1rem;
  margin: 0;
}

.demo-list dt {
  font-weight: 600;
  color: var(--text-muted, #64748b);
}

.demo-list dd {
  margin: 0;
}

.line-list,
.doc-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.line-list li,
.doc-list li {
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.doc-type {
  font-size: 0.75rem;
  margin-left: 0.5rem;
  color: var(--text-muted, #64748b);
}

.doc-summary {
  display: block;
  font-size: 0.875rem;
  color: var(--text-muted, #64748b);
  margin-top: 0.25rem;
}

.empty-hint,
.loading-state,
.error-state {
  color: var(--text-muted, #64748b);
}

.muted {
  color: var(--text-muted, #64748b);
}

.note-summary {
  margin: 0 0 0.25rem;
}

.soap-line {
  margin: 0.15rem 0;
  font-size: 0.8125rem;
  color: var(--text-muted, #64748b);
}
</style>
